import { ArrayField, Datagrid, FunctionField, TextField, useListContext } from "react-admin";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";
import { useNiyaazShowRelayColumn } from "./NiyaazShowMemberColumns";
import { NiyaazShowLabeledRow, NiyaazShowYesNo } from "./NiyaazShowField";
import PhoneLink from "@/components/PhoneLink";
import type { NiyaazShowMember } from "./niyaaz-show-types";

type NiyaazShowMemberListProps = {
  source: "familyMembers" | "mehmaans";
  showJamaat?: boolean;
  showWhatsApp?: boolean;
};

type NiyaazShowMemberMobileRowsProps = {
  members: NiyaazShowMember[];
  showJamaat: boolean;
  showWhatsApp: boolean;
  showRelay: boolean;
};

const emptyMembersText = (
  <Typography variant="body2" color="text.secondary">
    No members added
  </Typography>
);

const NiyaazShowMemberMobileRows = ({
  members,
  showJamaat,
  showWhatsApp,
  showRelay,
}: NiyaazShowMemberMobileRowsProps) => (
  <Stack divider={<Divider />} spacing={0}>
    {members.map((member, index) => (
      <Box key={member.its ?? `${member.name}-${index}`} sx={{ py: 1.5 }}>
        <NiyaazShowLabeledRow label="Name" value={member.name} valueFontWeight={600} />
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <NiyaazShowLabeledRow label="ITS" value={member.its} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <NiyaazShowLabeledRow label="Age" value={member.age} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <NiyaazShowLabeledRow label="Gender" value={member.gender} />
          </Grid>
          {showJamaat ? (
            <Grid size={12}>
              <NiyaazShowLabeledRow label="Jamaat" value={member.jamaat} />
            </Grid>
          ) : null}
          {showWhatsApp ? (
            <Grid size={12}>
              <NiyaazShowLabeledRow
                label="WhatsApp"
                value={member.whatsappNo ? <PhoneLink phone={member.whatsappNo} /> : "—"}
              />
            </Grid>
          ) : null}
          <Grid size={showRelay ? 6 : 12}>
            <NiyaazShowLabeledRow label="Chair" value={NiyaazShowYesNo(member.hasChair)} />
          </Grid>
          {showRelay ? (
            <Grid size={6}>
              <NiyaazShowLabeledRow label="Relay" value={NiyaazShowYesNo(member.hasRelay)} />
            </Grid>
          ) : null}
          {showRelay && member.hasRelay && member.relayReason ? (
            <Grid size={12}>
              <NiyaazShowLabeledRow label="Relay reason" value={member.relayReason} />
            </Grid>
          ) : null}
        </Grid>
      </Box>
    ))}
  </Stack>
);

const NiyaazShowMemberMobileList = ({
  showJamaat,
  showWhatsApp,
  showRelay,
}: {
  showJamaat: boolean;
  showWhatsApp: boolean;
  showRelay: boolean;
}) => {
  const { data, isLoading } = useListContext();

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading…
      </Typography>
    );
  }

  const members = (data ?? []) as NiyaazShowMember[];

  if (members.length === 0) {
    return emptyMembersText;
  }

  return (
    <NiyaazShowMemberMobileRows
      members={members}
      showJamaat={showJamaat}
      showWhatsApp={showWhatsApp}
      showRelay={showRelay}
    />
  );
};

const NiyaazShowMemberDatagrid = ({
  showJamaat,
  showWhatsApp,
  showRelay,
}: {
  showJamaat: boolean;
  showWhatsApp: boolean;
  showRelay: boolean;
}) => {
  const minWidth = showJamaat
    ? showRelay
      ? showWhatsApp
        ? 860
        : 720
      : showWhatsApp
        ? 800
        : 660
    : showRelay
      ? 620
      : 560;

  return (
    <Box sx={{ overflowX: "auto", width: "100%" }}>
      <Datagrid bulkActionButtons={false} empty={emptyMembersText} sx={{ minWidth }}>
        <TextField source="name" label="Name" />
        <TextField source="its" label="ITS" />
        <TextField source="age" label="Age" />
        <TextField source="gender" label="Gender" />
        {showJamaat ? <TextField source="jamaat" label="Jamaat" /> : null}
        {showWhatsApp ? (
          <FunctionField
            label="WhatsApp"
            source="whatsappNo"
            render={(record: NiyaazShowMember) => <PhoneLink phone={record.whatsappNo} empty="—" />}
          />
        ) : null}
        <FunctionField
          label="Chair"
          source="hasChair"
          render={(record: NiyaazShowMember) => NiyaazShowYesNo(record.hasChair)}
        />
        {showRelay ? (
          <>
            <FunctionField
              label="Relay"
              source="hasRelay"
              render={(record: NiyaazShowMember) => NiyaazShowYesNo(record.hasRelay)}
            />
            <TextField source="relayReason" label="Relay reason" emptyText="—" />
          </>
        ) : null}
      </Datagrid>
    </Box>
  );
};

export const NiyaazShowMemberList = ({
  source,
  showJamaat = false,
  showWhatsApp = false,
}: NiyaazShowMemberListProps) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const showRelay = useNiyaazShowRelayColumn();

  return (
    <ArrayField source={source}>
      {isSmall ? (
        <NiyaazShowMemberMobileList
          showJamaat={showJamaat}
          showWhatsApp={showWhatsApp}
          showRelay={showRelay}
        />
      ) : (
        <NiyaazShowMemberDatagrid
          showJamaat={showJamaat}
          showWhatsApp={showWhatsApp}
          showRelay={showRelay}
        />
      )}
    </ArrayField>
  );
};
